/**
 * The quote and sales-order routes, mounted through the seam in
 * `src/routes.extra.ts` — the shape `features/inquiries/routes.tsx` and
 * `features/admin/routes.infra.tsx` both use.
 *
 * ONE PROVIDER LAYOUT, and it is the toast provider, for the same reason the
 * inquiry routes have one: an action taken on the record (send, confirm, render)
 * raises a toast, and confirming a quote NAVIGATES — the toast that names the new
 * sales order must survive the move to another screen. A per-page provider would
 * unmount it mid-message.
 *
 * `ToastProvider` is imported from `features/inquiries/toast` rather than
 * duplicated. That is the same component and the same stack; two providers would
 * mean two stacks that can overlap each other at the bottom of the screen.
 *
 * The layout element itself is `./providers`, not a declaration in this file, so
 * that this module exports route metadata and nothing else — the shape
 * `features/admin/routes.infra.tsx` and `features/admin/routes.users.tsx`
 * already have. That is what keeps Fast Refresh working for the module; the
 * reason is stated once, in `providers.tsx`.
 *
 * All five paths sit inside `RequireCountry` + `AppShell`, so every screen can
 * rely on `useCountryCode()` and a resolved session.
 */

import type { RouteObject } from 'react-router-dom';
import { QuoteProviders } from './providers';
import { QuotesListPage } from './QuotesListPage';
import { QuoteBuilderPage } from './QuoteBuilderPage';
import { QuoteRecordPage } from './QuoteRecordPage';
import { SalesOrdersListPage } from './SalesOrdersListPage';
import { SalesOrderRecordPage } from './SalesOrderRecordPage';

export const quoteRoutes: RouteObject[] = [
  {
    element: <QuoteProviders />,
    children: [
      /*
       * `quotes/new` BEFORE `quotes/:quoteId`. React Router 7 ranks static
       * segments above dynamic ones, so this would resolve correctly either way —
       * but the order is written to match the intent rather than to rely on a
       * ranking rule a reader would have to look up.
       */
      { path: 'quotes', element: <QuotesListPage /> },
      { path: 'quotes/new', element: <QuoteBuilderPage /> },
      { path: 'quotes/:quoteId', element: <QuoteRecordPage /> },
      { path: 'sales-orders', element: <SalesOrdersListPage /> },
      /*
       * THE DIRECT ORDER — the SAME builder in `order` mode, not a second screen. It
       * writes a quote and confirms it in one interaction, which is the whole of the
       * feature: there is no SO create route to point at (`sales-orders.service.ts` §3),
       * and the API's own answer to "raise an order without sending a quote" was to
       * admit `draft` to `/confirm`.
       *
       * BEFORE `sales-orders/:soId`, for the reason `quotes/new` sits before
       * `quotes/:quoteId`: React Router 7 ranks static segments above dynamic ones, so
       * either order resolves — this one matches the intent without relying on a rule the
       * reader would have to go and look up.
       */
      { path: 'sales-orders/new', element: <QuoteBuilderPage mode="order" /> },
      { path: 'sales-orders/:soId', element: <SalesOrderRecordPage /> },
    ],
  },
];
